import { Component } from '@theme/component';

/**
 * @typedef {Object} CompleteTheSetRefs
 * @property {HTMLSelectElement[]} [optionSelect] - Per-card option selects (one per card option).
 * @property {HTMLSelectElement[]} [variantLookup] - Hidden lookup selects mapping option triplets to variant IDs.
 * @property {HTMLInputElement[]} [cardCheckbox] - Per-card "Add this to my order" checkboxes.
 * @property {HTMLLabelElement[]} [cardCheckboxLabel] - Wrappers around the checkboxes (for disabling state).
 * @property {HTMLElement[]} [cardPrice] - Per-card price displays.
 */

/**
 * <complete-the-set>
 *
 * Renders nothing on its own — the Liquid template fills it in. This component:
 *   1. Updates the chosen variant ID + price + availability when a customer changes an option select on a card.
 *   2. Exposes checked bonus line items so `product-form` can send one `POST /cart/add.js` with all lines.
 *      (A second JSON `fetch` was hitting Cloudflare 429 / bot challenges on trade.dusk.com.)
 *
 * @extends {Component<CompleteTheSetRefs>}
 */
class CompleteTheSet extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Use event delegation on the component for option-select changes (refs may be arrays).
    this.addEventListener('change', this.#onChange, { signal: this.#abortController.signal });

    // The parent <slideshow-component>'s scroller (<slideshow-slides>) attaches a bubble-phase
    // mousedown listener that calls event.preventDefault() to start its drag-to-scroll behavior.
    // That kills the native dropdown on <select> elements (and breaks checkbox toggles) inside
    // our cards. Each card sits *inside* the scroller, so we attach listeners on the card itself
    // and stopPropagation when the event originates on a form control — the event still reaches
    // the form control (it starts from there) but never bubbles up to the scroller.
    this.#attachCardListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Bonus variants to add with the main PDP line (same `/cart/add.js` request).
   * @returns {{ id: number, quantity: number }[]}
   */
  getCheckedBonusItems() {
    if (!this.customerLoggedIn) return [];
    return this.#collectCheckedItems();
  }

  /** Uncheck bonus cards after a successful combined add-to-cart. */
  clearCheckedBonusItems() {
    for (const cb of this.querySelectorAll('.complete-the-set__card-checkbox-input')) {
      if (cb instanceof HTMLInputElement) cb.checked = false;
    }
  }

  /** @returns {string | null} */
  get parentProductId() {
    return this.dataset.productId ?? null;
  }

  /** @returns {boolean} */
  get customerLoggedIn() {
    return this.dataset.customerLoggedIn === 'true';
  }

  /**
   * Attach pointer listeners to each card so we can stop events on form controls from reaching
   * the slideshow scroller's drag handler.
   */
  #attachCardListeners() {
    const cards = this.querySelectorAll('.complete-the-set__card');
    for (const card of cards) {
      for (const type of /** @type {const} */ (['mousedown', 'pointerdown', 'touchstart'])) {
        card.addEventListener(type, this.#stopPropagationOnFormControls, {
          signal: this.#abortController.signal,
        });
      }
    }
  }

  /**
   * Stop pointer events on form controls from reaching the parent slideshow's drag handler,
   * which calls preventDefault() on mousedown and breaks <select> dropdowns and checkbox toggles.
   * @param {Event} event
   */
  #stopPropagationOnFormControls = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target || !(target instanceof Element)) return;
    if (target.closest('select, input, button, textarea, label, a')) {
      event.stopPropagation();
    }
  };

  /**
   * Handle option select changes on any card.
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.classList.contains('complete-the-set__card-option-select')) return;

    const card = target.closest('.complete-the-set__card');
    if (!(card instanceof HTMLElement)) return;

    this.#syncCardVariant(card);
  };

  /**
   * Read the option selects on a card, find the matching variant in the hidden lookup,
   * and update the price + checkbox value + disabled state accordingly.
   * @param {HTMLElement} card
   */
  #syncCardVariant(card) {
    const optionSelects = /** @type {NodeListOf<HTMLSelectElement>} */ (
      card.querySelectorAll('.complete-the-set__card-option-select')
    );
    const variantLookup = /** @type {HTMLSelectElement | null} */ (
      card.querySelector('.complete-the-set__card-variant-lookup')
    );
    const checkbox = /** @type {HTMLInputElement | null} */ (card.querySelector('.complete-the-set__card-checkbox-input'));
    const checkboxLabel = card.querySelector('.complete-the-set__card-checkbox');
    const priceEl = card.querySelector('.complete-the-set__card-price-amount');
    const vatNoteEl = card.querySelector('.complete-the-set__card-vat-note');

    if (!variantLookup || !checkbox) return;

    /** @type {Record<string, string>} */
    const chosen = {};
    for (const select of optionSelects) {
      const position = select.dataset.optionPosition;
      if (position) chosen[position] = select.value;
    }

    /** @type {HTMLOptionElement | null} */
    let matchedOption = null;
    for (const option of variantLookup.options) {
      let allMatch = true;
      for (const [pos, val] of Object.entries(chosen)) {
        if (option.dataset[`option${pos}`] !== val) {
          allMatch = false;
          break;
        }
      }
      if (allMatch) {
        matchedOption = option;
        break;
      }
    }

    if (!matchedOption) {
      // No variant matches the option combination — disable the checkbox.
      checkbox.checked = false;
      checkbox.disabled = true;
      checkboxLabel?.setAttribute('data-disabled', 'true');
      return;
    }

    const variantId = matchedOption.value;
    const isAvailable = matchedOption.dataset.available === 'true';
    const priceCents = matchedOption.dataset.price;

    checkbox.value = variantId;
    checkbox.disabled = !isAvailable;
    if (checkboxLabel) {
      checkboxLabel.setAttribute('data-disabled', String(!isAvailable));
    }

    if (priceEl && priceCents) {
      priceEl.textContent = formatMoney(Number(priceCents));
    }
    if (vatNoteEl && matchedOption) {
      vatNoteEl.hidden = matchedOption.dataset.taxable !== 'true';
    }
  }

  /**
   * @returns {{ id: number, quantity: number }[]}
   */
  #collectCheckedItems() {
    /** @type {{ id: number, quantity: number }[]} */
    const items = [];
    const checkboxes = this.querySelectorAll('.complete-the-set__card-checkbox-input');

    for (const cb of checkboxes) {
      if (!(cb instanceof HTMLInputElement)) continue;
      if (!cb.checked || cb.disabled) continue;
      const id = Number(cb.value);
      if (!Number.isFinite(id) || id <= 0) continue;
      items.push({ id, quantity: 1 });
    }

    return items;
  }
}

/**
 * Format a price in cents to the shop's currency string.
 * Uses the global Shopify.formatMoney if available, else falls back to a minimal formatter.
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
  const w = /** @type {any} */ (window);
  if (w.Shopify?.formatMoney && w.Shopify?.currency?.active) {
    try {
      return w.Shopify.formatMoney(cents, w.Shopify.money_format);
    } catch {
      // fall through
    }
  }
  // Minimal fallback: assume 2 decimal places.
  return (cents / 100).toFixed(2);
}

if (!customElements.get('complete-the-set')) {
  customElements.define('complete-the-set', CompleteTheSet);
}
